import styled from '@emotion/styled';
import type { SelectChangeEvent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { FunctionComponent } from 'react';

import type { LanguageCode, LanguageConfigMap } from '~/shared/internationalization';

import { RequiredSectionTitle } from './RequiredSectionTitle';

const StyledFormControl = styled(FormControl)({
    width: '100%',
});

const StyledSelect = styled(Select)({
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fc531f',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#fc531f',
    },
    '.MuiOutlinedInput-notchedOutline': {
        borderColor: '#e7e7e7',
        borderRadius: '0.625rem',
        borderWidth: '0.0625rem',
    },
    '.MuiSelect-select': {
        alignItems: 'center',
        background: '#fbfbff',
        boxSizing: 'border-box',
        color: '#040f16',
        display: 'flex',
        fontFamily: 'FT Regola Neue',
        fontSize: '0.875rem',
        fontWeight: 400,
        height: '2.5rem',
        lineHeight: '1.25rem',
        paddingBottom: '0.5rem',
        paddingLeft: '0.375rem',
        paddingRight: '0.75rem',
        paddingTop: '0.5rem',
    },
    '.MuiSvgIcon-root': {
        color: '#6e6e6c',
    },
});

const StyledMenuItem = styled(MenuItem)({
    '&:hover': {
        backgroundColor: '#f5f5f5',
    },
    color: '#040f16',
    fontFamily: 'FT Regola Neue',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: '1.25rem',
    padding: '0.5rem 0.75rem',
});

type LanguageSelectorProps = {
    readonly availableLanguages: LanguageConfigMap;
    readonly label: string;
    readonly onChange: (language: LanguageCode) => void;
    readonly required?: boolean;
    readonly selectedLanguage: LanguageCode;
};

export const LanguageSelector: FunctionComponent<LanguageSelectorProps> = ({
    availableLanguages,
    label,
    onChange,
    required = false,
    selectedLanguage,
}) => {
    const handleChange = (event: SelectChangeEvent): void => {
        onChange(event.target.value as LanguageCode);
    };

    return (
        <StyledFormControl>
            <RequiredSectionTitle required={required}>{label}</RequiredSectionTitle>
            <StyledSelect displayEmpty={true} value={selectedLanguage} onChange={handleChange}>
                {Object.values(availableLanguages).map((language) => (
                    <StyledMenuItem key={language.code} value={language.code}>
                        {language.name}
                    </StyledMenuItem>
                ))}
            </StyledSelect>
        </StyledFormControl>
    );
};
